import { useState, useMemo } from 'react'
import { RouterLink as Link } from "../../../router";
import { Zap, Bot, Plug } from "../../../icons"
import { SessionBadges } from './session-badges'
import { formatCost, formatDuration, formatDate, projectDisplayName } from '@lens/lib/decode'
import { Card, CardContent, CardHeader, CardTitle } from "@excellent/ui"
import { Input } from "@excellent/ui"
import { Tabs, TabsList, TabsTrigger } from "@excellent/ui"
import type { SessionWithFacet } from '@lens/types/claude'
import { DataTable, type DataTableColumn } from '../../data-table'

const PAGE_SIZE = 25

type Key = 'start_time' | 'duration_minutes' | 'total_messages' | 'estimated_cost' | 'tool_calls' | 'project' | 'flags'
type FilterTab = 'all' | 'compacted' | 'agent' | 'mcp'

interface Props {
  sessions: SessionWithFacet[]
  scopedProject?: { slug: string; name: string } | null
}

const messageCount = (s: SessionWithFacet) =>
  (s.user_message_count ?? 0) + (s.assistant_message_count ?? 0)

const toolCount = (s: SessionWithFacet) =>
  Object.values(s.tool_counts ?? {}).reduce((sum, c) => sum + c, 0)

export function SessionTable({ sessions, scopedProject = null }: Props) {
  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    let s = sessions
    if (filterTab === 'compacted') s = s.filter((x) => x.has_compaction)
    if (filterTab === 'agent')     s = s.filter((x) => x.uses_task_agent)
    if (filterTab === 'mcp')       s = s.filter((x) => x.uses_mcp)
    if (search) {
      const q = search.toLowerCase()
      s = s.filter((x) =>
        (!scopedProject && x.project_path?.toLowerCase().includes(q)) ||
        x.first_prompt?.toLowerCase().includes(q) ||
        x.slug?.toLowerCase().includes(q),
      )
    }
    return s
  }, [sessions, filterTab, search, scopedProject])

  const columns: DataTableColumn<SessionWithFacet>[] = useMemo(() => [
    {
      key: 'start_time',
      header: 'Date',
      sort: (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
      defaultDesc: true,
      render: (s) =>(
        <span className="type-body whitespace-nowrap">{formatDate(s.start_time)}</span>
      ),
    },
    {
      key: 'project',
      header: 'Project',
      hidden: !!scopedProject,
      sort: (a, b) =>
        projectDisplayName(a.project_path ?? '').toLowerCase().localeCompare(
          projectDisplayName(b.project_path ?? '').toLowerCase(),
        ),
      cellClassName: 'max-w-[240px]',
      render: (s) => (
        <>
          <Link
            href={`/lens/sessions/${s.session_id}`}
            className="text-foreground hover:opacity-60 transition-opacity truncate block"
            title={s.project_path ?? ''}
          >
            {projectDisplayName(s.project_path ?? '')}
          </Link>
          {s.first_prompt && (
            <p className="text-foreground truncate type-body">
              {s.first_prompt.slice(0, 60)}
            </p>
          )}
        </>
      ),
    },
    {
      key: 'duration_minutes',
      header: 'Duration',
      sort: (a, b) => (a.duration_minutes ?? 0) - (b.duration_minutes ?? 0),
      defaultDesc: true,
      render: (s) =>formatDuration(s.duration_minutes ?? 0),
    },
    {
      key: 'total_messages',
      header: 'Messages',
      sort: (a, b) => messageCount(a) - messageCount(b),
      defaultDesc: true,
      render: (s) =>messageCount(s).toLocaleString(),
    },
    {
      key: 'tool_calls',
      header: 'Tools',
      sort: (a, b) => toolCount(a) - toolCount(b),
      defaultDesc: true,
      render: (s) =>toolCount(s).toLocaleString(),
    },
    {
      key: 'estimated_cost',
      header: 'Cost',
      sort: (a, b) => (a.estimated_cost ?? 0) - (b.estimated_cost ?? 0),
      defaultDesc: true,
      render: (s) =>formatCost(s.estimated_cost),
    },
    {
      key: 'flags',
      header: 'Flags',
      sortable: false,
      headerClassName: 'text-muted-foreground',
      render: (s) =>(
        <SessionBadges
          has_compaction={s.has_compaction}
          uses_task_agent={s.uses_task_agent}
          uses_mcp={s.uses_mcp}
          uses_web_search={s.uses_web_search}
          uses_web_fetch={s.uses_web_fetch}
          has_thinking={s.has_thinking}
        />
      ),
    },
  ], [scopedProject])

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>Sessions</CardTitle>
          <Tabs value={filterTab} onValueChange={(v) => setFilterTab(v as FilterTab)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="compacted" className="gap-1.5">
                <Zap className="w-3 h-3" strokeWidth={1.5} /> Compacted
              </TabsTrigger>
              <TabsTrigger value="agent" className="gap-1.5">
                <Bot className="w-3 h-3" strokeWidth={1.5} /> Agent
              </TabsTrigger>
              <TabsTrigger value="mcp" className="gap-1.5">
                <Plug className="w-3 h-3" strokeWidth={1.5} /> MCP
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <Input
            placeholder={scopedProject ? "Search prompt…" : "Search project or prompt…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <span className="type-body text-foreground">
            {filtered.length.toLocaleString()} sessions
          </span>
        </div>

        {/* pad-ignore: not a double-pad — tableClassName below zeroes the
            table's own first/last-cell padding, so CardContent's px-6 stays
            the single gutter (VERTICAL-RHYTHM-PRD R8). */}
        <DataTable
          wrapInCard={false}
          columns={columns}
          rows={filtered}
          rowKey={(s) => s.session_id}
          initialSort={{ key: 'start_time', dir: 'desc' }}
          rowHref={(s) => `/lens/sessions/${s.session_id}`}
          empty="No sessions match filters"
          pageSize={PAGE_SIZE}
          keyboardNav
          tableClassName="[&_tr>*:first-child]:pl-0 [&_tr>*:last-child]:pr-0"
        />
      </CardContent>
    </Card>
  )
}
